import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function Toggle({ on, onChange, label, sub }) {
  return (
    <div className="toggle-row">
      <div className="tr-left">
        <div className="tr-label">{label}</div>
        {sub && <div className="tr-sub">{sub}</div>}
      </div>
      <motion.button
        className={`toggle-btn ${on ? 'on' : ''}`}
        onClick={() => onChange(!on)}
        whileTap={{ scale: .92 }}
      />
    </div>
  );
}

function ProgBar({ total, target, label }) {
  if (!target) return null;
  const pct = Math.min(Math.round((total/target)*100), 100);
  const over = Math.round((total/target)*100);
  const cls = over>=100?'ok':over>=70?'warn':'bad';
  return (
    <div className="prog-wrap">
      <div className="prog-top">
        <span className="prog-lbl">{label}: {total}/{target}</span>
        <span className={`prog-pct ${cls}`}>{over}%</span>
      </div>
      <div className="prog-bar">
        <motion.div
          className={`prog-fill ${cls}`}
          initial={{ width:0 }}
          animate={{ width: pct+'%' }}
          transition={{ duration:.6, ease:[.4,0,.2,1], delay:.15 }}
        />
      </div>
    </div>
  );
}

function Section({ icon, title, badge, children, defaultOpen=true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="ds-card">
      <div className={`ds-card-header ${open?'open':''}`} onClick={() => setOpen(o=>!o)}>
        <div className="ds-icon" style={{ background:'var(--surface2)' }}>{icon}</div>
        <div className="ds-title">{title}</div>
        {badge && <div className="ds-status">{badge}</div>}
        <motion.span className="ds-chevron" animate={{ rotate: open?180:0 }} transition={{ duration:.2 }}>
          ⌄
        </motion.span>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height:0, opacity:0 }}
            animate={{ height:'auto', opacity:1 }}
            exit={{ height:0, opacity:0 }}
            transition={{ duration:.25, ease:[.4,0,.2,1] }}
            style={{ overflow:'hidden' }}
          >
            <div className="ds-body">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TaskItem({ task, onUpdate, onDelete }) {
  return (
    <motion.div className="task-item" layout initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, scale:.97 }}>
      <button
        className={`task-check ${task.status==='done'?'on':''}`}
        onClick={() => onUpdate({ status: task.status==='done'?'pending':'done' })}
      >
        {task.status==='done' && <svg width="11" height="11" viewBox="0 0 11 11"><polyline points="1.5,5.5 4.5,8.5 9.5,2.5" fill="none" stroke="#000" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}
      </button>
      <span className={`task-text ${task.status==='done'?'done':''}`}>{task.text}</span>
      <select
        className={`task-status ${task.status}`}
        value={task.status}
        onChange={e => onUpdate({ status: e.target.value })}
        onClick={e => e.stopPropagation()}
        style={{ appearance:'none', cursor:'pointer' }}
      >
        <option value="pending">Pending</option>
        <option value="in-progress">In progress</option>
        <option value="done">Done</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <button className="task-del" onClick={onDelete}>×</button>
    </motion.div>
  );
}

export default function BrandDetail({ store, brandId, navigate }) {
  const { state, getMonthRecord, setMonthRecord, addTask, updateTask, deleteTask } = store;
  const { brands, managers, currentMonth, specialDays } = state;
  const brand = brands.find(b => b.id === brandId);
  if (!brand) return null;
  const mgr = managers.find(m => m.id === brand.managerId);
  const rec = getMonthRecord(brandId, currentMonth);
  const monthSDs = specialDays.filter(sd => sd.month === currentMonth);
  const [newTask, setNewTask] = useState('');

  const patch = (field, value) => setMonthRecord(brandId, currentMonth, { [field]: value });
  const patchD = (key, val) => {
    const d = rec.deliverables||{};
    setMonthRecord(brandId, currentMonth, { deliverables: { ...d, [key]: parseInt(val)||0 } });
  };
  const patchWU = (week, val) => {
    const wu = rec.weeklyUpdates||{};
    setMonthRecord(brandId, currentMonth, { weeklyUpdates: { ...wu, [week]: val } });
  };
  const patchSD = (id, val) => {
    const sd = rec.specialDaysDone||{};
    setMonthRecord(brandId, currentMonth, { specialDaysDone: { ...sd, [id]: val } });
  };

  const d = rec.deliverables||{};
  const totalPosts  = (d.w1Posts||0)+(d.w2Posts||0)+(d.w3Posts||0)+(d.w4Posts||0);
  const totalVideos = (d.w1Videos||0)+(d.w2Videos||0)+(d.w3Videos||0)+(d.w4Videos||0);
  const wu = rec.weeklyUpdates||{};
  const tasks = rec.tasks||[];
  const pending = tasks.filter(t=>t.status!=='done'&&t.status!=='cancelled').length;
  const weeks = ['w1','w2','w3','w4'];
  const fmtM = currentMonth ? new Date(currentMonth+'-01').toLocaleDateString('en-GB',{month:'long',year:'numeric'}) : '';

  const handleAddTask = () => {
    if (!newTask.trim()) return;
    addTask(brandId, currentMonth, newTask.trim());
    setNewTask('');
  };

  const cpDone = rec.contentPlanToDesigner && rec.contentPlanToClient;
  const allKpisDone = cpDone && rec.monthlyReportSent;

  return (
    <div>
      <button className="bd-back" onClick={() => navigate('manager', { manager: brand.managerId })}>
        ‹ {mgr?.name}
      </button>
      <div className="bd-header">
        <div>
          <h1 className="page-title" style={{ marginBottom:2 }}>{brand.name}</h1>
          <p className="page-sub" style={{ marginBottom:0 }}>
            {fmtM} · {brand.targetPosts} posts / {brand.targetVideos} videos per month
          </p>
        </div>
        {allKpisDone && (
          <motion.span initial={{scale:0}} animate={{scale:1}} className="badge green" style={{ fontSize:13, padding:'6px 14px' }}>
            ✓ All done
          </motion.span>
        )}
      </div>

      <div className="detail-sections">

        {/* Content Plans */}
        <Section icon="📋" title="Content plans" badge={
          cpDone
            ? <span className="badge green">Done</span>
            : <span className="badge amber">Pending</span>
        }>
          <Toggle on={rec.contentPlanToDesigner} onChange={v=>patch('contentPlanToDesigner',v)}
            label="Sent to designer" sub="Due by 10th of the month" />
          <Toggle on={rec.contentPlanToClient} onChange={v=>patch('contentPlanToClient',v)}
            label="Sent to client (with designs)" sub="Due by 25th of the month" />
        </Section>

        {/* Monthly Report */}
        <Section icon="📊" title="Monthly report" badge={
          rec.monthlyReportSent
            ? <span className="badge green">Sent</span>
            : <span className="badge red">Not sent</span>
        }>
          <Toggle on={rec.monthlyReportSent} onChange={v=>patch('monthlyReportSent',v)}
            label="Monthly report sent to client" sub="Due by 5th of the month" />
        </Section>

        {/* Weekly Updates */}
        <Section icon="📬" title="Friday weekly updates" badge={
          <span className={`badge ${Object.values(wu).filter(Boolean).length===4?'green':'amber'}`}>
            {Object.values(wu).filter(Boolean).length}/4 sent
          </span>
        }>
          <div className="week-row">
            {weeks.map((w, i) => (
              <motion.button key={w} className={`week-btn ${wu[w]?'on':''}`}
                onClick={() => patchWU(w, !wu[w])}
                whileTap={{ scale:.94 }}
              >
                <span className="wb-label">Week {i+1}</span>
                <span className="wb-check">{wu[w]?'✓':'○'}</span>
              </motion.button>
            ))}
          </div>
        </Section>

        {/* Special Days */}
        {monthSDs.length > 0 && (
          <Section icon="🗓" title="Special days">
            {monthSDs.map(sd => (
              <Toggle key={sd.id}
                on={rec.specialDaysDone?.[sd.id]||false}
                onChange={v=>patchSD(sd.id,v)}
                label={sd.name}
                sub={new Date(sd.date).toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short'})}
              />
            ))}
          </Section>
        )}

        {/* Deliverables */}
        <Section icon="📦" title="Monthly deliverables" badge={
          brand.targetPosts > 0 ? (
            <span className={`badge ${totalPosts>=brand.targetPosts?'green':totalPosts>0?'amber':'red'}`}>
              {Math.round((totalPosts/brand.targetPosts)*100)}%
            </span>
          ) : null
        }>
          <ProgBar total={totalPosts}  target={brand.targetPosts}  label="Posts" />
          {brand.targetVideos > 0 && (
            <ProgBar total={totalVideos} target={brand.targetVideos} label="Videos" />
          )}
          <div className="week-inputs" style={{ marginTop:16 }}>
            {weeks.map((w,i) => (
              <div key={w} className="wi-col">
                <div className="wi-label">W{i+1}</div>
                <div className="wi-group">
                  <div className="wi-sublabel">Posts</div>
                  <input type="number" min="0" className="wi-input"
                    value={d[`${w}Posts`]||0}
                    onChange={e=>patchD(`${w}Posts`,e.target.value)}
                  />
                </div>
                {brand.targetVideos > 0 && (
                  <div className="wi-group">
                    <div className="wi-sublabel">Videos</div>
                    <input type="number" min="0" className="wi-input"
                      value={d[`${w}Videos`]||0}
                      onChange={e=>patchD(`${w}Videos`,e.target.value)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* Tasks */}
        <Section icon="✅" title="Tasks" defaultOpen={true} badge={
          pending > 0
            ? <span className="badge amber">{pending} open</span>
            : tasks.length > 0 ? <span className="badge green">All done</span> : null
        }>
          <AnimatePresence>
            <div className="task-list">
              {tasks.length === 0 && (
                <p style={{ fontSize:14, color:'var(--text3)', padding:'8px 0' }}>No tasks yet. Add one below.</p>
              )}
              {tasks.map(t => (
                <TaskItem key={t.id} task={t}
                  onUpdate={p => updateTask(brandId, currentMonth, t.id, p)}
                  onDelete={() => deleteTask(brandId, currentMonth, t.id)}
                />
              ))}
            </div>
          </AnimatePresence>
          <div className="task-add">
            <input className="task-input" placeholder="Add a task… press Enter"
              value={newTask} onChange={e=>setNewTask(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleAddTask()}
            />
            <motion.button className="btn btn-accent" whileTap={{scale:.96}} onClick={handleAddTask}>Add</motion.button>
          </div>
        </Section>
      </div>
    </div>
  );
}
